import { EventFilter } from '@shared/enums/EventFilter';
import { EventOrder } from '@shared/enums/EventOrder';
import { EventType } from '@shared/enums/EventType';

const URL_PRESET_FILTER_KEYS = Object.freeze([
	EventFilter.NAME_SEARCH,
	EventFilter.UPCOMING,
	EventFilter.LIVE,
	EventFilter.ENDED,
	EventFilter.LIVE_AND_UPCOMING,
	EventFilter.CONTENT_TYPE,
	EventFilter.LABEL,
	EventFilter.LABEL_MATCH_ALL,
	EventFilter.BETWEEN,
]);

const FLAG_FILTER_KEYS = new Set([
	EventFilter.UPCOMING,
	EventFilter.LIVE,
	EventFilter.ENDED,
	EventFilter.LIVE_AND_UPCOMING,
]);

const LABEL_FILTER_KEYS = new Set([
	EventFilter.LABEL,
	EventFilter.LABEL_MATCH_ALL,
]);

const SUPPORTED_CONTENT_TYPES = new Set(Object.values(EventType));
const SUPPORTED_SORT_DIRECTIONS = new Set(['asc', 'desc']);

const objectFromParams = (params) => {
	if (params instanceof URLSearchParams) {
		return params;
	}

	return new URLSearchParams(params || '');
};

const parseJsonObject = (searchParams, key) => {
	if (!searchParams.has(key)) {
		return null;
	}

	try {
		const parsedValue = JSON.parse(searchParams.get(key));
		if (parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue)) {
			return parsedValue;
		}
	} catch (_error) {
		return null;
	}

	return null;
};

const sanitizeString = (value) => {
	if (typeof value !== 'string') {
		return null;
	}

	const normalizedValue = value.trim();
	return normalizedValue.length > 0 ? normalizedValue : null;
};

const sanitizeLabelValues = (value) => {
	const values = Array.isArray(value) ? value : [value];
	const sanitizedValues = values.filter(labelValue => (
		Number.isInteger(labelValue) ||
		(typeof labelValue === 'string' && labelValue.trim().length > 0)
	));

	if (sanitizedValues.length === 0) {
		return null;
	}

	return Array.isArray(value) ? sanitizedValues : sanitizedValues[0];
};

const sanitizeContentType = (value) => {
	const values = Array.isArray(value) ? value : [value];
	const sanitizedValues = values.filter(contentType => SUPPORTED_CONTENT_TYPES.has(contentType));

	if (sanitizedValues.length === 0) {
		return null;
	}

	return Array.isArray(value) ? sanitizedValues : sanitizedValues[0];
};

const sanitizeBetween = (value) => {
	if (!Array.isArray(value) || value.length !== 2) {
		return null;
	}

	const startValue = sanitizeString(value[0]);
	const endValue = sanitizeString(value[1]);
	if (!startValue || !endValue) {
		return null;
	}

	return [startValue, endValue];
};

export const sanitizeVideoCenterUrlPresetFilters = (filters) => {
	if (!filters || typeof filters !== 'object' || Array.isArray(filters)) {
		return null;
	}

	const sanitizedFilters = {};

	for (const filterKey of URL_PRESET_FILTER_KEYS) {
		if (!Object.prototype.hasOwnProperty.call(filters, filterKey)) {
			continue;
		}

		if (FLAG_FILTER_KEYS.has(filterKey)) {
			sanitizedFilters[filterKey] = null;
			continue;
		}

		if (filterKey === EventFilter.NAME_SEARCH) {
			const value = sanitizeString(filters[filterKey]);
			if (value) {
				sanitizedFilters[filterKey] = value;
			}
			continue;
		}

		if (filterKey === EventFilter.CONTENT_TYPE) {
			const value = sanitizeContentType(filters[filterKey]);
			if (value) {
				sanitizedFilters[filterKey] = value;
			}
			continue;
		}

		if (LABEL_FILTER_KEYS.has(filterKey)) {
			const value = sanitizeLabelValues(filters[filterKey]);
			if (value) {
				sanitizedFilters[filterKey] = value;
			}
			continue;
		}

		if (filterKey === EventFilter.BETWEEN) {
			const value = sanitizeBetween(filters[filterKey]);
			if (value) {
				sanitizedFilters[filterKey] = value;
			}
		}
	}

	return Object.keys(sanitizedFilters).length > 0 ? sanitizedFilters : null;
};

export const sanitizeVideoCenterUrlPresetSortOrder = (sortOrder) => {
	if (!sortOrder || typeof sortOrder !== 'object' || Array.isArray(sortOrder)) {
		return null;
	}

	const direction = sanitizeString(sortOrder[EventOrder.START])?.toLowerCase();
	if (!SUPPORTED_SORT_DIRECTIONS.has(direction)) {
		return null;
	}

	return {
		[EventOrder.START]: direction,
	};
};

export const parseVideoCenterUrlPreset = (params) => {
	const searchParams = objectFromParams(params);
	const filters = searchParams.has('filters')
		? sanitizeVideoCenterUrlPresetFilters(parseJsonObject(searchParams, 'filters'))
		: null;

	return {
		filters,
		sortOrder: sanitizeVideoCenterUrlPresetSortOrder(parseJsonObject(searchParams, 'sortOrder')),
	};
};
